import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "../../contexts/ApiContext";
import BracketScreen from "../bracket/BracketScreen";
import DoubleBracketScreen from "../bracket/DoubleBracketScreen";
import { createBracket, createSeededBracket } from "../../utils/bracketLogic";
import {
  createDoubleBracket,
  createSeededDoubleBracket,
  hydrateDouble,
} from "../../utils/doubleElimination";
import "../../styles/crud.css";

// Loads a tournament's saved bracket and runs it live (new or resumed).
function PlayTournament() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { tournamentsApi, playersApi } = useApi();

  const {
    data: tournament,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["tournament", id],
    queryFn: () => tournamentsApi.getById(id),
  });

  const { data: players = [] } = useQuery({
    queryKey: ["players"],
    queryFn: () => playersApi.getAll(),
  });

  const [live, setLive] = useState(null); // { format, init } | { broken }

  useEffect(() => {
    if (!tournament) return;
    let parsed = null;
    try {
      parsed = tournament.bracketState
        ? JSON.parse(tournament.bracketState)
        : null;
    } catch {
      parsed = null;
    }
    if (!parsed) return setLive({ broken: true });
    if (parsed.format === "double") {
      setLive({ format: "double", init: hydrateDouble(parsed.de) });
    } else {
      setLive({ format: "single", init: parsed.rounds });
    }
  }, [tournament]);

  if (isLoading || (!live && !isError)) {
    return (
      <div className="crud-page">
        <p className="crud-loading">Loading bracket...</p>
      </div>
    );
  }

  if (isError || !tournament || live?.broken) {
    return (
      <div className="crud-page">
        <div className="crud-header">
          <h1 className="crud-title">Tournament</h1>
        </div>
        <p className="tn-err">
          This tournament has no saved bracket to continue.
        </p>
        <button
          className="crud-btn-ghost"
          onClick={() => navigate("/app/tournaments")}
        >
          ← Back to Tournaments
        </button>
      </div>
    );
  }

  // Player names + rating order for re-shuffling (only used before play starts).
  const nameById = {};
  players.forEach((p) => (nameById[p.id] = p.name));
  const playerNames = (tournament.playerIds || [])
    .map((pid) => nameById[pid])
    .filter(Boolean);
  const ranked = players
    .filter((p) => playerNames.includes(p.name))
    .slice()
    .sort((a, b) => (b.elo ?? -1) - (a.elo ?? -1))
    .map((p) => ({ name: p.name, elo: p.elo ?? null }));

  const canShuffle = playerNames.length >= 2;
  function reshuffle() {
    if (!canShuffle) return;
    if (live.format === "double")
      setLive({ format: "double", init: createDoubleBracket(playerNames) });
    else setLive({ format: "single", init: createBracket(playerNames) });
  }
  function seededShuffle() {
    if (!canShuffle) return;
    if (live.format === "double")
      setLive({
        format: "double",
        init: createSeededDoubleBracket(ranked, playerNames),
      });
    else
      setLive({
        format: "single",
        init: createSeededBracket(ranked, playerNames),
      });
  }

  const common = {
    tournament,
    onReshuffle: canShuffle ? reshuffle : undefined,
    onSeededShuffle: canShuffle ? seededShuffle : undefined,
    onReset: () => navigate("/app/tournaments"),
    onComplete: () => navigate("/app/tournaments"),
    backLabel: "← Tournaments",
  };

  return live.format === "double" ? (
    <DoubleBracketScreen initState={live.init} {...common} />
  ) : (
    <BracketScreen initRounds={live.init} {...common} />
  );
}

export default PlayTournament;
